// IndexNow integration component for automatic search engine indexing
import React, { useEffect, useState } from 'react';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { submitToIndexNow, submitNewPages, submitAllPages, autoSubmitIndexNow } from '../utils/indexNow';

const IndexNowIntegration = ({ 
  autoSubmit = true, 
  submitOnMount = true, 
  pagesToSubmit = [],
  showStatus = false 
}) => {
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-submit on component mount (only in production)
  useEffect(() => {
    // Skip IndexNow in development mode
    if (process.env.NODE_ENV !== 'production') {
      return;
    }
    
    if (submitOnMount) {
      handleAutoSubmit();
    }
  }, [submitOnMount]);

  // Auto-submit for new content (only in production)
  useEffect(() => {
    // Skip IndexNow in development mode
    if (process.env.NODE_ENV !== 'production') {
      return;
    }
    
    if (autoSubmit) {
      autoSubmitIndexNow();
    }
  }, [autoSubmit]);

  const handleAutoSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Submit new location pages
      const result = await submitNewPages();
      setSubmissionStatus(result);
    } catch (error) {
      setSubmissionStatus({ success: false, error: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitAll = async () => {
    setIsSubmitting(true);
    try {
      const result = await submitAllPages();
      setSubmissionStatus(result);
    } catch (error) {
      setSubmissionStatus({ success: false, error: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitCustom = async () => {
    if (pagesToSubmit.length === 0) return;
    
    setIsSubmitting(true);
    try {
      const result = await submitToIndexNow(pagesToSubmit);
      setSubmissionStatus(result);
    } catch (error) {
      setSubmissionStatus({ success: false, error: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render anything if showStatus is false
  if (!showStatus) {
    return null;
  }

  return (
    <div className="indexnow-integration" style={{ 
      position: 'fixed', 
      bottom: '20px', 
      right: '20px', 
      background: 'white', 
      padding: '15px', 
      borderRadius: '8px', 
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      zIndex: 1000,
      maxWidth: '300px',
      fontSize: '14px'
    }}>
      <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>IndexNow Status</h4>
      
      {isSubmitting && (
        <p style={{ color: '#007bff', margin: '5px 0' }}>
          Submitting to search engines...
        </p>
      )}
      
      {submissionStatus && (
        <div style={{ margin: '10px 0' }}>
          {submissionStatus.success ? (
            <p style={{ color: '#28a745', margin: '5px 0' }}>
              <FaCheckCircle /> Successfully submitted to search engines
            </p>
          ) : (
            <p style={{ color: '#dc3545', margin: '5px 0' }}>
              <FaTimesCircle /> Submission failed: {submissionStatus.error}
            </p>
          )}
          
          {submissionStatus.results && (
            <div style={{ marginTop: '10px' }}>
              <strong>Results:</strong>
              {submissionStatus.results.map((result, index) => (
                <div key={index} style={{ 
                  fontSize: '12px', 
                  margin: '2px 0',
                  color: result.success ? '#28a745' : '#dc3545'
                }}>
                  {result.endpoint}: {result.message}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      <div style={{ marginTop: '10px' }}>
        <button 
          onClick={handleSubmitAll}
          disabled={isSubmitting}
          style={{
            background: '#007bff',
            color: 'white',
            border: 'none',
            padding: '8px 12px',
            borderRadius: '4px',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            marginRight: '5px',
            fontSize: '12px'
          }}
        >
          Submit All Pages
        </button>
        
        {pagesToSubmit.length > 0 && (
          <button 
            onClick={handleSubmitCustom}
            disabled={isSubmitting}
            style={{
              background: '#28a745',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '4px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontSize: '12px'
            }}
          >
            Submit Custom
          </button>
        )}
      </div>
      
      <div style={{ 
        marginTop: '10px', 
        fontSize: '11px', 
        color: '#666',
        borderTop: '1px solid #eee',
        paddingTop: '8px'
      }}>
        <p style={{ margin: '2px 0' }}>
          <strong>IndexNow:</strong> Instant search engine indexing
        </p>
        <p style={{ margin: '2px 0' }}>
          <strong>Supported:</strong> Bing, Yandex, IndexNow.org
        </p>
      </div>
    </div>
  );
};

export default IndexNowIntegration;
